import React, { useState } from 'react';
import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import defaultPlaylistThumbnail from '../../assets/default_playlist_thumbnail.svg';
import CommentCount from '../common/CommentCount';
import AlbumCountWithDate from '../common/AlbumCountWithDate';
import UserWithProfile from '../common/UserWithProfile';
import CardTitle from '../common/CardTitle';
import theme from '../../theme';
import { trackHomeContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, REFERRER_TYPE } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';
import defaultUserThumbnail from '../../assets/default_user_thumbnail.svg';
import GradeIcon from '../common/GradeIcon';

const PlaylistVerticalCard = ({
  playlist,
  dateOnNewLine = false,
  actionButton,
  hideUser = false,
  hideMeta = false,
  variant = 'default',
  referrerType,
  referrerSection,
  homeSection,
  thumbnailVariant = 'default',
  titleLineClamp = 2,
  coverActionButton,
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const [imageError, setImageError] = useState(false);

  const playlistUrl = `/lists/${playlist.slug || playlist.id}`;
  const navigateState = homeSection
    ? { referrerType: REFERRER_TYPE.HOME }
    : referrerType
      ? { referrerType, referrerSection }
      : undefined;

  const handleLinkToPlaylist = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (homeSection) {
        trackHomeContentClick(
          {
            section: homeSection,
            contentType: CONTENT_TYPE.LIST,
            contentId: playlist.id,
            contentName: playlist.title,
          },
          user,
        );
      }
      navigateWithScrollMemory(playlistUrl, navigateState ? { state: navigateState } : undefined);
    }
  };

  const handleUserClick = (userNickname) => {
    navigateWithScrollMemory(`/users/${encodeURIComponent(userNickname)}`);
  };

  const handleError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const thumbnailAspectRatio =
    thumbnailVariant === 'vertical'
      ? '1 / 1.34'
      : thumbnailVariant === 'square'
        ? '1 / 1'
        : '280 / 188';

  if (variant === 'overlay') {
    return (
      <Box
        component={RouterLink}
        to={playlistUrl}
        state={navigateState}
        onClick={handleLinkToPlaylist}
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          aspectRatio: { xs: '1 / 1.35', md: '1 / 1.34', lg: '1 / 1.36' },
          overflow: 'hidden',
          color: theme.palette.common.white,
          backgroundColor: theme.palette.black[9],
          textDecoration: 'none',
          '&:hover img': {
            transform: 'scale(1.03)',
          },
        }}
      >
        <Box
          component="img"
          src={imageError || !playlist.imageUrl ? defaultPlaylistThumbnail : playlist.imageUrl}
          alt={imageError || !playlist.imageUrl ? 'Default playlist thumbnail' : playlist.title}
          onError={handleError}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 180ms ease',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.16) 44%, rgba(0,0,0,0.76) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 1.5, md: 1.75, lg: 2 },
            minWidth: 0,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              minHeight: 0,
              pb: { xs: 2.75, md: 3, lg: 3.25 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 10, lg: 11 },
                fontWeight: 700,
                lineHeight: 1,
                mb: { xs: 0.75, lg: 1 },
                color: 'rgba(255,255,255,0.86)',
                textShadow: '0 1px 8px rgba(0,0,0,0.4)',
                flexShrink: 0,
              }}
            >
              MIX
            </Typography>
            <Typography
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                fontSize: { xs: 13, md: 14, lg: 15 },
                fontWeight: 800,
                lineHeight: 1.25,
                minHeight: '2.5em',
                color: theme.palette.common.white,
                textShadow: '0 2px 12px rgba(0,0,0,0.55)',
                flexShrink: 0,
              }}
            >
              {playlist.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={playlist.userProfileImageUrl || defaultUserThumbnail}
              sx={{
                width: { xs: 22, md: 24 },
                height: { xs: 22, md: 24 },
                border: '1px solid rgba(255,255,255,0.36)',
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  overflow: 'hidden',
                  fontSize: { xs: 12, md: 13 },
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: 'rgba(255,255,255,0.72)',
                  textShadow: '0 1px 8px rgba(0,0,0,0.45)',
                  minWidth: 0,
                }}
              >
                {playlist.username}
              </Typography>
              <GradeIcon grade={playlist.userGrade} size={{ xs: 12, md: 13 }} />
            </Box>
          </Stack>
          {actionButton && (
            <Box
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              sx={{
                position: 'absolute',
                right: { xs: 10, md: 12 },
                bottom: { xs: 10, md: 12 },
                zIndex: 2,
                '& .MuiIconButton-root': {
                  width: { xs: 30, md: 34 },
                  height: { xs: 30, md: 34 },
                  borderColor: 'rgba(255,255,255,0.4)',
                  backgroundColor: 'rgba(0,0,0,0.42)',
                  color: theme.palette.common.white,
                },
              }}
            >
              {actionButton}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: { xs: 1.25, md: 1.25, lg: 1.5 },
      }}
    >
      {/* 이미지 */}
      <Box
        component={RouterLink}
        to={playlistUrl}
        state={navigateState}
        onClick={handleLinkToPlaylist}
        sx={{ display: 'block', width: '100%', flexShrink: 0, position: 'relative' }}
      >
        <Box
          component="img"
          src={imageError || !playlist.imageUrl ? defaultPlaylistThumbnail : playlist.imageUrl}
          alt={imageError || !playlist.imageUrl ? 'Default playlist thumbnail' : playlist.title}
          onError={handleError}
          sx={{
            width: '100%',
            aspectRatio: thumbnailAspectRatio,
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
            border: `1px solid ${theme.palette.grey.line}`,
          }}
        />
        {coverActionButton && (
          <Box
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            sx={{
              position: 'absolute',
              top: { xs: 8, md: 10 },
              right: { xs: 8, md: 10 },
              zIndex: 3,
            }}
          >
            {coverActionButton}
          </Box>
        )}
      </Box>

      {/* 정보 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <CardTitle
          title={playlist.title}
          to={playlistUrl}
          onClick={handleLinkToPlaylist}
          sx={{
            WebkitLineClamp: titleLineClamp,
            ...(titleLineClamp === 1
              ? {
                  whiteSpace: 'nowrap',
                  display: 'block',
                }
              : {}),
          }}
        />
        {!hideUser && (
          <UserWithProfile
            variant="small"
            username={playlist.username}
            profileImageUrl={playlist.userProfileImageUrl}
            userNickname={playlist.username}
            grade={playlist.userGrade}
            onClick={handleUserClick}
            sx={{ mt: { xs: homeSection ? 0.875 : 1.25, md: 1, lg: 1.25 } }}
          />
        )}

        {!hideMeta && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              width: '100%',
              mt: { xs: 1.25, md: 1, lg: 1.25 },
            }}
          >
            {/* 좌측: 앨범수, 날짜, 댓글 */}
            <Box>
              <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <AlbumCountWithDate
                  albumCount={playlist.type === 'TRACK' ? playlist.trackCount : playlist.albumCount}
                  date={!dateOnNewLine && playlist.createdAt}
                  type={playlist.type}
                />
                {dateOnNewLine && (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                    <Typography
                      fontSize={{ xs: 12, md: 13, lg: 14 }}
                      fontWeight="regular"
                      sx={{
                        color: theme.palette.black[4],
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {playlist.createdAt}
                    </Typography>
                  </>
                )}
                {playlist.createdAt && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '8px',
                      alignSelf: 'center',
                      backgroundColor: theme.palette.grey.line,
                    }}
                  />
                )}
                <CommentCount commentCount={playlist.commentCount} />
              </Box>
            </Box>

            {/* 우측 하단: 버튼 */}
            {actionButton}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlaylistVerticalCard;
